import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientProfile } from '../../../interfaces/common';
import CustomButton from "../../../components/CustomButton";
import Checkbox from "../../../components/Checkbox";
import TextLink from "../../../components/TextLink";
import ChevronLeftIcon from "../../../components/Icons/ChevronLeftIcon";

interface Props {
  profile: ClientProfile;
  save: (profile: ClientProfile) => void;
  goBack: () => void;
}

const StepFour: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const [coachingAreas, setCoachingAreas] = useState<Array<string>>(props.profile.coachingAreas);

  // Separate lists of options
  const leadershipAndTeamDevelopment = [
    { name: 'executive_coaching', label: t('executive_coaching') },
    { name: 'team_coaching', label: t('team_coaching') },
    { name: 'leadership_coaching', label: t('leadership_coaching') },
  ];

  const organisationalAndBusinessCoaching = [
    { name: 'business_coaching', label: t('business_coaching') },
    { name: 'agile_coaching', label: t('agile_coaching') },
    { name: 'inclusive_corporate_culture', label: t('inclusive_corporate_culture') },
  ];

  const personalDevelopment = [
    { name: 'lifestyle_health', label: t('lifestyle_health') },
    { name: 'stress_management', label: t('stress_management') },
    { name: 'life_coaching', label: t('life_coaching') },
    { name: 'mental_training', label: t('mental_training') },
    { name: 'self_leadership', label: t('self_leadership') },
    { name: 'rehabilitation', label: t('rehabilitation') },
  ];

  const careerAndProfessionalGrowth = [
    { name: 'career_coaching', label: t('career_coaching') },
    { name: 'transit_coaching', label: t('transit_coaching') },
    { name: 'mentorship', label: t('mentorship') },
  ];

  // If no areas are selected, form is not valid
  const notValid = () => coachingAreas.length === 0;

  const handleSave = () => {
    const newProfile: ClientProfile = { ...props.profile };
    newProfile.coachingAreas = coachingAreas;
    props.save(newProfile);
  };

  // Toggle function for adding/removing an area
  const toggleArea = (area: string) => {
    if (coachingAreas.includes(area)) {
      // Remove the area
      const newAreas = coachingAreas.filter(a => a !== area);
      setCoachingAreas(newAreas);
    } else {
      // Add the area
      const newAreas = [...coachingAreas, area];
      setCoachingAreas(newAreas);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="mt-6 z-heading2">
          {t('coachingAreas')}
        </div>

        <div className="mt-2 text-gray-400">
          {t('chooseCoachingAreas')}
        </div>
      </div>

      {/* Leadership and Team Development */}
      <div>
        <div className="z-heading2">
          {t('leadership_and_team_development')}
        </div>
        <div className="flex flex-col gap-3 mt-2">
          {leadershipAndTeamDevelopment.map((option, index) => (
            <div key={option.name}>
              <Checkbox
                id={option.name}
                checked={coachingAreas.includes(option.name)}
                onChange={() => toggleArea(option.name)}
                label={option.label}
              />
            </div>
          ))}
        </div>
      </div>

      <hr className="border-indigo-700" />

      {/* Organisational and Business Coaching */}
      <div>
        <div className="z-heading2">
          {t('organisational_and_business_coaching')}
        </div>
        <div className="flex flex-col gap-3 mt-2">
          {organisationalAndBusinessCoaching.map((option, index) => (
            <div key={option.name}>
              <Checkbox
                id={option.name}
                checked={coachingAreas.includes(option.name)}
                onChange={() => toggleArea(option.name)}
                label={option.label}
              />
            </div>
          ))}
        </div>
      </div>

      <hr className="border-indigo-700" />

      {/* Personal Development */}
      <div>
        <div className="z-heading2">
          {t('personal_development')}
        </div>
        <div className="flex flex-col gap-3 mt-2">
          {personalDevelopment.map((option, index) => (
            <div key={option.name}>
              <Checkbox
                id={option.name}
                checked={coachingAreas.includes(option.name)}
                onChange={() => toggleArea(option.name)}
                label={option.label}
              />
            </div>
          ))}
        </div>
      </div>

      <hr className="border-indigo-700" />

      {/* Career and Professional Growth */}
      <div>
        <div className="z-heading2">
          {t('career_and_professional_growth')}
        </div>
        <div className="flex flex-col gap-3 mt-2">
          {careerAndProfessionalGrowth.map((option, index) => (
            <div key={option.name}>
              <Checkbox
                id={option.name}
                checked={coachingAreas.includes(option.name)}
                onChange={() => toggleArea(option.name)}
                label={option.label}
              />
            </div>
          ))}
        </div>
      </div>

      <hr className="border-indigo-700" />

      <div className="flex flex-col gap-4">
        <CustomButton className="mt-6" onClick={handleSave} disabled={coachingAreas.length === 0}>
          {t('next')}
        </CustomButton>

        {/* Back Link */}
        <TextLink
          color="white"
          onClick={props.goBack}
          leftIcon={
            <ChevronLeftIcon className="h-4 w-4 text-white!" aria-hidden="true" />
          }
          size="sm"
        >
          {t('back')}
        </TextLink>
      </div>
    </div>
  );
};

export default StepFour;
